/**
 * Static data + shared row types for the Team-Chat module (1:1 with the
 * prototype: channels L450–454, DMs L458–462, `teamMessages` L2674–2680 of
 * Hausverwaltung Hero.dc.html).
 */

import type { Translate } from "@/lib/i18n/translate";
import { ld } from "@/lib/theme/light-dark";

/** A direct-message row in the sidebar (round initials avatar + presence dot). */
export type DmRow = {
  readonly initials: string;
  readonly avBg: string;
  readonly avFg: string;
  readonly statusDot: string;
  readonly name: string;
};

/** DM rows above the active group thread (Tobias, Carla). */
export function dmRowsTop(t: Translate): readonly DmRow[] {
  return [
    {
      initials: "TB",
      avBg: ld("#2A2F3A", "#e0e4e9"),
      avFg: "#fff",
      statusDot: ld("#C2C7CE", "#505458"),
      name: t("tobiasBerger"),
    },
    {
      initials: "CV",
      avBg: ld("#EEE9F7", "#35303c"),
      avFg: ld("#6D5BA6", "#ae9ded"),
      statusDot: "#3A9E6B",
      name: t("carlaVogt"),
    },
  ];
}

/** DM rows below the active group thread (Jana, Markus). */
export function dmRowsBottom(t: Translate): readonly DmRow[] {
  return [
    {
      initials: "JP",
      avBg: ld("#F6E9EE", "#392e33"),
      avFg: ld("#B05B79", "#e68ba9"),
      statusDot: "#E0A500",
      name: t("janaPohl"),
    },
    {
      initials: "MF",
      avBg: ld("#E7EEF8", "#2c323a"),
      avFg: ld("#3F6CB0", "#7cacf5"),
      statusDot: ld("#C2C7CE", "#505458"),
      name: t("markusFrey"),
    },
  ];
}

/**
 * Sidebar channel handles (rendered with a leading "#").
 * `t` is `useTranslations("marketing.moduleTeamchatData")`.
 */
export function channels(t: Translate): readonly string[] {
  return [
    t("kanalAllgemein"),
    t("kanalInstandhaltung"),
    t("kanalVermietung"),
    t("kanalBuchhaltung"),
    t("kanalEtvOrga"),
  ];
}

/** A thread entry — either a date separator or a chat message. */
export type TeamEntry =
  | { readonly kind: "date"; readonly date: string }
  | {
      readonly kind: "msg";
      readonly author: string;
      readonly time: string;
      readonly avInit: string;
      readonly avBg: string;
      readonly avFg: string;
      readonly statusDot: string;
      readonly text: string;
    };

/** A chat message (the non-date variant of `TeamEntry`). */
export type TeamMessage = Extract<TeamEntry, { readonly kind: "msg" }>;

/** `t` is `useTranslations("marketing.moduleTeamchatData")`. */
export function teamMessages(t: Translate): readonly TeamEntry[] {
  return [
    { kind: "date", date: t("gestern") },
    {
      kind: "msg",
      author: "Carla Vogt",
      time: "16:22",
      avInit: "CV",
      avBg: ld("#EEE9F7", "#35303c"),
      avFg: ld("#6D5BA6", "#ae9ded"),
      statusDot: "#3A9E6B",
      text: t("nachrichtEigentuemerversammlung"),
    },
    {
      kind: "msg",
      author: "David Lehmann",
      time: "16:35",
      avInit: "DL",
      avBg: ld("#E7EEF8", "#2c323a"),
      avFg: ld("#3F6CB0", "#7cacf5"),
      statusDot: "#3A9E6B",
      text: t("nachrichtEinladungenProtokoll"),
    },
    { kind: "date", date: t("heute") },
    {
      kind: "msg",
      author: "Nina Hartmann",
      time: "09:48",
      avInit: "NH",
      avBg: ld("#0B0F19", "#fafafa"),
      avFg: "#ffffff",
      statusDot: "#3A9E6B",
      text: t("nachrichtHausgeldabrechnung"),
    },
  ];
}
